// IAStudioConsumptionStats.jsx — Dashboard de consommation IA Studio (Vidéo + Image)
// Combien de générations et combien d'Ariary consommés, filtrable par jour/semaine/mois/
// période personnalisée. Demande explicite de Hery le 2026-08-20.

import React, { useState, useEffect, useCallback } from 'react';
import { Coins, Film, ImageIcon, Sparkles, Loader2, Calendar, RefreshCw, TrendingDown, TrendingUp } from 'lucide-react';
import { API_URL } from '../../config';

const authHeaders = () => ({ 'Authorization': `Bearer ${localStorage.getItem('auth_token')}` });

const toISODate = (d) => d.toISOString().slice(0, 10);
const today = () => new Date();

const RANGE_PRESETS = [
  {
    id: 'today', label: "Aujourd'hui",
    range: () => { const t = today(); return [toISODate(t), toISODate(t)]; },
  },
  {
    id: 'week', label: 'Cette semaine',
    range: () => {
      const t = today();
      const day = (t.getDay() + 6) % 7; // lundi = 0
      const monday = new Date(t); monday.setDate(t.getDate() - day);
      return [toISODate(monday), toISODate(t)];
    },
  },
  {
    id: 'month', label: 'Ce mois',
    range: () => {
      const t = today();
      const first = new Date(t.getFullYear(), t.getMonth(), 1);
      return [toISODate(first), toISODate(t)];
    },
  },
  { id: 'custom', label: 'Personnalisé', range: null },
];

const EMPTY = { count: 0, charged_ar: 0, refunded_ar: 0, net_ar: 0 };

function BigStatCard({ icon: Icon, title, subtitle, count, netAr, gradient, glow }) {
  return (
    <div
      className={`relative overflow-hidden rounded-3xl p-6 text-white bg-gradient-to-br ${gradient} shadow-2xl`}
      style={{ boxShadow: glow }}
    >
      <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/10 blur-3xl" />
      <div className="absolute -bottom-14 -left-10 w-40 h-40 rounded-full bg-black/10 blur-3xl" />
      <div className="relative z-10">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center">
            <Icon className="w-6 h-6" />
          </div>
          <div>
            <div className="font-bold text-lg leading-tight">{title}</div>
            <div className="text-white/70 text-xs">{subtitle}</div>
          </div>
        </div>
        <div className="text-3xl md:text-4xl font-black mb-1">{netAr.toLocaleString('fr-FR')} <span className="text-lg font-bold">Ar</span></div>
        <div className="text-white/80 text-sm">{count} génération{count > 1 ? 's' : ''}</div>
      </div>
    </div>
  );
}

function BreakdownRow({ label, stats, accent }) {
  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
      <div className="flex items-center justify-between mb-3">
        <span className={`font-bold text-sm ${accent}`}>{label}</span>
        <span className="text-gray-400 text-xs">{stats.count} génération{stats.count > 1 ? 's' : ''}</span>
      </div>
      <div className="grid grid-cols-3 gap-3 text-center">
        <div>
          <div className="flex items-center justify-center gap-1 text-emerald-400 text-xs font-semibold mb-1">
            <TrendingUp className="w-3.5 h-3.5" /> Facturé
          </div>
          <div className="text-white font-bold text-sm">{stats.charged_ar.toLocaleString('fr-FR')} Ar</div>
        </div>
        <div>
          <div className="flex items-center justify-center gap-1 text-red-400 text-xs font-semibold mb-1">
            <TrendingDown className="w-3.5 h-3.5" /> Remboursé
          </div>
          <div className="text-white font-bold text-sm">{stats.refunded_ar.toLocaleString('fr-FR')} Ar</div>
        </div>
        <div>
          <div className="flex items-center justify-center gap-1 text-amber-400 text-xs font-semibold mb-1">
            <Coins className="w-3.5 h-3.5" /> Net
          </div>
          <div className="text-white font-bold text-sm">{stats.net_ar.toLocaleString('fr-FR')} Ar</div>
        </div>
      </div>
    </div>
  );
}

export default function IAStudioConsumptionStats() {
  const [preset, setPreset] = useState('today');
  const [customFrom, setCustomFrom] = useState(toISODate(today()));
  const [customTo, setCustomTo] = useState(toISODate(today()));
  const [video, setVideo] = useState(EMPTY);
  const [image, setImage] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const activeRange = useCallback(() => {
    const p = RANGE_PRESETS.find(r => r.id === preset);
    if (p && p.range) return p.range();
    return [customFrom, customTo];
  }, [preset, customFrom, customTo]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [from, to] = activeRange();
      const res = await fetch(`${API_URL}/api/ia-studio/admin/consumption-stats?from=${from}&to=${to}`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) {
        setVideo(data.video);
        setImage(data.image);
      } else {
        setError(data.error || 'Erreur de chargement');
      }
    } catch (e) {
      setError('Erreur réseau');
    }
    setLoading(false);
  }, [activeRange]);

  useEffect(() => { load(); }, [load]);

  const totalCount = video.count + image.count;
  const totalNet = video.net_ar + image.net_ar;
  const [from, to] = activeRange();

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-2 flex items-center gap-3">
            <Sparkles className="w-7 h-7 md:w-8 md:h-8 text-amber-400" /> Consommation IA Studio
          </h1>
          <p className="text-sm md:text-base text-blue-200">Vidéo + Image · ce qui a été généré et consommé en Ariary</p>
        </div>
        <button
          onClick={load} disabled={loading}
          className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm font-semibold transition-all disabled:opacity-50 self-start sm:self-auto"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          Actualiser
        </button>
      </div>

      {/* Sélecteur de période */}
      <div className="bg-black/30 border border-white/10 rounded-2xl p-3 md:p-4">
        <div className="flex flex-wrap gap-2 mb-3">
          {RANGE_PRESETS.map(p => (
            <button
              key={p.id}
              onClick={() => setPreset(p.id)}
              className={`px-4 py-2 rounded-xl text-sm font-bold transition-all ${
                preset === p.id
                  ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg'
                  : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        {preset === 'custom' && (
          <div className="flex flex-wrap items-center gap-3 pt-1">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-gray-400" />
              <label className="text-gray-400 text-xs font-semibold">Du</label>
              <input
                type="date" value={customFrom} max={customTo}
                onChange={e => setCustomFrom(e.target.value)}
                className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-amber-400"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-gray-400 text-xs font-semibold">Au</label>
              <input
                type="date" value={customTo} min={customFrom} max={toISODate(today())}
                onChange={e => setCustomTo(e.target.value)}
                className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-white text-sm focus:outline-none focus:border-amber-400"
              />
            </div>
          </div>
        )}
        <p className="text-gray-500 text-xs mt-3">
          Période : <span className="text-gray-300 font-semibold">{from}</span> → <span className="text-gray-300 font-semibold">{to}</span>
        </p>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-4 text-red-300 text-sm">{error}</div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
        </div>
      ) : (
        <>
          {/* Grandes cartes */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            <BigStatCard
              icon={Coins} title="Total consommé" subtitle="Vidéo + Image"
              count={totalCount} netAr={totalNet}
              gradient="from-amber-500 via-orange-500 to-rose-600"
              glow="0 0 50px rgba(245,158,11,0.25)"
            />
            <BigStatCard
              icon={Film} title="TeckiA Vidéo" subtitle="Générations vidéo"
              count={video.count} netAr={video.net_ar}
              gradient="from-fuchsia-600 via-purple-600 to-indigo-700"
              glow="0 0 50px rgba(168,85,247,0.25)"
            />
            <BigStatCard
              icon={ImageIcon} title="TeckiA Image" subtitle="Générations image"
              count={image.count} netAr={image.net_ar}
              gradient="from-cyan-500 via-blue-600 to-indigo-700"
              glow="0 0 50px rgba(56,189,248,0.25)"
            />
          </div>

          {/* Détail facturé / remboursé / net */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <BreakdownRow label="TeckiA Vidéo — détail" stats={video} accent="text-fuchsia-300" />
            <BreakdownRow label="TeckiA Image — détail" stats={image} accent="text-cyan-300" />
          </div>
        </>
      )}
    </div>
  );
}
